import { useState, type ReactNode } from 'react';
import {
  medHoldRuleAdd, medHoldRuleRetire, medHoldRules, medPackageAdd, medPackageRetire, medPackages, medPassPolicyGet, medPassPolicySet,
  medWristbands, residentWristband,
  type ApiError, type HoldComparator, type HoldVital, type MedPackage, type MedPassPolicy, type PackageCodeType,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { BuildingLoading, PickOneBuilding, useBuilding } from './building';
import { ErrLine, I, btn, chip, pre, sx, useApi, useGuarded, usePage } from './kit';

/**
 * Settings > Med pass (2026-10-13, approved by Andrew): the building's set-up for giving
 * medicines by scanning, in one place, as comparable eMARs keep it (ALIS "Settings >
 * Medications"; research 2026-10-13, section 4):
 *
 *   Scanning and strict mode   GET/PUT /med-pass/policy (the skip reasons too)
 *   Pharmacy feed              said plainly: not connected yet; typing a code is the fallback
 *   Resident wristbands        GET /med-pass/wristbands, PUT /residents/:id/wristband
 *   Package and Rx codes       GET/POST /med-pass/packages, POST /med-pass/packages/:id/retire
 *   Hold rules                 GET/POST /med-pass/hold-rules, POST /med-pass/hold-rules/:id/retire
 *
 * Every write is the building's Facility Admin's (the server refuses anyone else). Super Admin
 * and Operator see one picked building read only; with a place picked, not one building, they
 * are asked to pick one first.
 */
export function MedPassSection() {
  const building = useBuilding();
  if (building.mode === 'loading') return <BuildingLoading />;
  if (!building.orgId) return <PickOneBuilding what="Med pass" />;
  return <MedPass key={building.orgId} facility={building.mode === 'picked' ? building.orgId : undefined} name={building.name} />;
}

const WRAP = 'box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;max-width:980px;display:flex;flex-direction:column;gap:12px;';
const HEAD = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const NOTE = 'font-size:16px;color:#54606C;line-height:1.5;max-width:80ch;';
const ROW = 'display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding:9px 0;border-top:1px solid rgba(34,39,46,.08);';
const INPUT = 'box-sizing:border-box;height:30px;padding:0 9px;border-radius:7px;border:1px solid rgba(34,39,46,.22);background:#FCFAF4;color:#22272E;font-size:16px;font-family:inherit;min-width:0;';
const BLOCK = 'display:flex;flex-direction:column;gap:6px;padding:12px 14px;border:1px solid rgba(34,39,46,.12);border-radius:10px;';

const CODE_TYPE: Record<PackageCodeType, string> = { rx: 'Rx number', ndc: 'NDC', label: 'Label barcode' };
const VITALS: [HoldVital, string][] = [['pulse', 'Pulse'], ['sbp', 'Systolic BP'], ['dbp', 'Diastolic BP'], ['temp', 'Temperature'], ['spo2', 'Oxygen (SpO2)']];
const COMPARATORS: [HoldComparator, string][] = [['<', 'below'], ['<=', 'at or below'], ['>', 'above'], ['>=', 'at or above']];

function Block({ title, testId, children }: { title: string; testId: string; children: ReactNode }) {
  return (
    <section data-testid={testId} aria-label={title} style={sx(BLOCK)}>
      <span style={sx(HEAD)}>{title}</span>
      {children}
    </section>
  );
}

function Switch({ on, label, disabled, title, onToggle }: { on: boolean; label: string; disabled: boolean; title?: string; onToggle: () => void }) {
  return (
    <a
      href="#"
      role="switch"
      aria-checked={on}
      aria-label={label}
      aria-disabled={disabled || undefined}
      title={title}
      onClick={(e) => {
        pre(e);
        if (!disabled) onToggle();
      }}
      style={sx(
        'display:inline-flex;align-items:center;width:42px;height:24px;flex:0 0 42px;border-radius:14px;padding:2px;box-sizing:border-box;text-decoration:none;' +
          (disabled ? 'opacity:.55;cursor:not-allowed;' : '') +
          (on ? 'background:#1F6B52;justify-content:flex-end;' : 'background:rgba(34,39,46,.22);justify-content:flex-start;'),
      )}
    >
      <span style={sx('display:block;width:20px;height:20px;border-radius:50%;background:#FDFDFD;box-shadow:0 1px 3px rgba(34,39,46,.28);')} />
    </a>
  );
}

function MedPass({ facility, name }: { facility: string | undefined; name: string | null }) {
  const { role } = useConsoleRole();
  const { canWrite, roReason } = useGuarded();
  const { changed } = usePage();
  // Only the building's Facility Admin writes here (med-tools.ts); the team reads.
  const admin = role === 'ADMIN';
  const canEdit = canWrite && admin;
  const why = !admin ? 'Read only: the building’s Facility Admin sets up the med pass.' : roReason;
  const query = facility ? { facility } : {};
  const policy = useApi((c, s) => medPassPolicyGet(c, { query, signal: s }), [facility]);
  if (!policy.data && !policy.error) return <Skeleton rows={6} label="Loading the med pass set-up" />;
  return (
    <div data-screen-label="Med pass" data-testid="settings-med-pass" style={sx(WRAP)}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;flex:0 0 7px;')} />
        <span style={sx(HEAD)}>{'Med pass · ' + (name ?? 'this building')}</span>
      </div>
      <div style={sx(NOTE)}>
        How this building gives medicines by scanning: whether the scan is used, whether a dose may be given without one, the codes on each resident’s wristband and on each package, and the readings that hold a dose.
      </div>
      {why && (
        <div role="status" data-testid="med-pass-readonly" style={sx('font-size:16px;font-weight:600;color:#8A5A00;')}>
          {I(why)}
        </div>
      )}
      <ErrLine error={policy.error} />
      {policy.data && <ScanPolicy policy={policy.data} canEdit={canEdit} why={why} onSaved={() => { policy.reload(); changed(); }} />}
      {policy.data && <PharmacyFeed policy={policy.data} />}
      <Wristbands query={query} facility={facility} canEdit={canEdit} />
      <Packages query={query} facility={facility} canEdit={canEdit} why={why} />
      <HoldRules query={query} facility={facility} canEdit={canEdit} why={why} sample={policy.data?.sampleHoldRules ?? true} onPolicy={() => { policy.reload(); changed(); }} />
    </div>
  );
}

// ── Scanning and strict mode ──────────────────────────────────────────────────

type PolicyPatch = { scanning?: boolean; requireScan?: boolean; scanSkipReasons?: string[]; sampleHoldRules?: boolean };

function ScanPolicy({ policy, canEdit, why, onSaved }: { policy: MedPassPolicy; canEdit: boolean; why: string | null; onSaved: () => void }) {
  const { client } = useSession();
  const [err, setErr] = useState<ApiError | null>(null);
  const [reason, setReason] = useState('');
  const save = async (body: PolicyPatch): Promise<boolean> => {
    if (!canEdit) return false;
    setErr(null);
    try {
      await medPassPolicySet(client, { body });
      onSaved();
      return true;
    } catch (e) {
      setErr(e as ApiError);
      return false;
    }
  };
  const reasons = policy.scanSkipReasons;
  return (
    <Block title="Scanning" testId="med-pass-scanning">
      <div style={sx(ROW + 'border-top:none;padding-top:0;')}>
        <span style={sx('flex:1 1 300px;min-width:0;')}>
          <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>Scan the wristband and the package</span>
          <span style={sx('display:block;font-size:16px;color:#54606C;line-height:1.45;')}>
            {policy.scanning
              ? 'On: nurses can scan the resident’s wristband and the package before a dose, and the five rights are checked against the order.'
              : 'Off: the scan is not used in this building, and nurses record each dose with one tap.'}
          </span>
        </span>
        <Switch on={policy.scanning} label={'Scanning ' + (policy.scanning ? 'on' : 'off')} disabled={!canEdit} title={why ?? undefined} onToggle={() => void save({ scanning: !policy.scanning })} />
      </div>
      <div style={sx(ROW)}>
        <span style={sx('flex:1 1 300px;min-width:0;')}>
          <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>Strict mode</span>
          <span style={sx('display:block;font-size:16px;color:#54606C;line-height:1.45;')}>
            {!policy.scanning
              ? 'Needs scanning on.'
              : policy.requireScan
                ? reasons.length
                  ? 'On: every dose is scanned before it is recorded as given. A dose that will not scan is given only with a reason from the list below, and the reason stays on the record.'
                  : 'On, with no reasons on the list: every dose must scan before it is recorded as given. There is no way around the scan.'
                : 'Off (commonly recommended): scanning is offered, and a dose can still be recorded as given without a scan, with no reason needed.'}
          </span>
        </span>
        <Switch
          on={policy.requireScan}
          label={'Strict mode ' + (policy.requireScan ? 'on' : 'off')}
          disabled={!canEdit || !policy.scanning}
          title={why ?? (!policy.scanning ? 'Turn scanning on first.' : undefined)}
          onToggle={() => void save({ requireScan: !policy.requireScan })}
        />
      </div>
      <div style={sx('padding-top:6px;border-top:1px solid rgba(34,39,46,.08);display:flex;flex-direction:column;gap:5px;')} data-testid="med-pass-skip-reasons">
        <span style={sx('font-size:16px;font-weight:700;color:#3C454F;')}>Reasons a nurse may pick to give without a scan (strict mode)</span>
        {reasons.length === 0 && <span style={sx('font-size:16px;color:#54606C;')}>No reasons: in strict mode a dose that will not scan cannot be recorded as given.</span>}
        {reasons.map((r) => (
          <span key={r} style={sx('display:flex;align-items:center;gap:10px;font-size:16px;color:#22272E;')}>
            <span style={sx('flex:1 1 auto;min-width:0;')}>{r}</span>
            {canEdit && (
              <a href="#" aria-label={'Remove reason: ' + r} onClick={(e) => { pre(e); void save({ scanSkipReasons: reasons.filter((x) => x !== r) }); }} style={sx(btn('ghost') + 'height:26px;padding:0 10px;font-size:16px;')}>
                Remove
              </a>
            )}
          </span>
        ))}
        {canEdit && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const r = reason.trim();
              if (!r) return;
              void save({ scanSkipReasons: [...reasons, r] }).then((ok) => { if (ok) setReason(''); });
            }}
            style={sx('display:flex;gap:8px;flex-wrap:wrap;align-items:center;')}
          >
            <input aria-label="New reason" value={reason} maxLength={80} placeholder="e.g. The barcode will not scan" onChange={(e) => setReason(e.target.value)} style={sx(INPUT + 'flex:1 1 260px;')} />
            <button type="submit" style={sx(btn('soft') + 'cursor:pointer;font-family:inherit;')}>Add reason</button>
          </form>
        )}
      </div>
      <ErrLine error={err} />
    </Block>
  );
}

function PharmacyFeed({ policy }: { policy: MedPassPolicy }) {
  return (
    <div data-testid="med-pass-pharmacy" style={sx('display:flex;align-items:flex-start;gap:10px;flex-wrap:wrap;')}>
      <span style={sx(policy.pharmacyFeed.connected ? chip('#12503C', 'rgba(47,138,111,.16)') : chip('#8A5A00', 'rgba(138,90,0,.12)'))}>
        {policy.pharmacyFeed.connected ? 'PHARMACY FEED CONNECTED' : 'PHARMACY FEED: NOT CONNECTED YET'}
      </span>
      <span style={sx(NOTE + 'flex:1 1 300px;')}>{policy.pharmacyFeed.text}</span>
    </div>
  );
}

// ── Resident wristbands ───────────────────────────────────────────────────────

type Q = { facility?: string };

function Wristbands({ query, facility, canEdit }: { query: Q; facility: string | undefined; canEdit: boolean }) {
  const { client } = useSession();
  const list = useApi((c, s) => medWristbands(c, { query, signal: s }), [facility]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [err, setErr] = useState<{ id: string; e: ApiError } | null>(null);
  const set = async (residentId: string) => {
    const code = (draft[residentId] ?? '').trim();
    if (!canEdit || !code) return;
    setErr(null);
    try {
      await residentWristband(client, { params: { id: residentId }, body: { code } });
      setDraft((d) => ({ ...d, [residentId]: '' }));
      list.reload();
    } catch (e) {
      setErr({ id: residentId, e: e as ApiError });
    }
  };
  return (
    <Block title="Resident wristbands" testId="med-pass-wristbands">
      <span style={sx(NOTE)}>The code printed on each resident’s band. A scan of the band names the resident; two residents can never share a code.</span>
      {!list.data && !list.error && <Skeleton rows={2} label="Loading the wristbands" />}
      <ErrLine error={list.error} />
      {list.data?.residents.length === 0 && <span style={sx('font-size:16px;color:#54606C;')}>No residents in this building yet.</span>}
      {list.data?.residents.map((r) => (
        <div key={r.residentId} data-testid={`wristband-${r.residentId}`} style={sx(ROW)}>
          <span style={sx('flex:1 1 200px;min-width:0;font-size:16px;font-weight:600;color:#22272E;')}>{I(r.name ?? 'Resident')}</span>
          <span style={sx('flex:0 1 180px;font-size:16px;font-variant-numeric:tabular-nums;color:' + (r.code ? '#22272E' : '#8A5A00') + ';')}>{r.code ?? 'Not set'}</span>
          {canEdit ? (
            <form onSubmit={(e) => { e.preventDefault(); void set(r.residentId); }} style={sx('display:flex;gap:6px;flex:1 1 260px;')}>
              <input aria-label={'Wristband code for ' + (r.name ?? 'resident')} value={draft[r.residentId] ?? ''} maxLength={120} placeholder="Scan or type the band" onChange={(e) => setDraft((d) => ({ ...d, [r.residentId]: e.target.value }))} style={sx(INPUT + 'flex:1 1 auto;')} />
              <button type="submit" style={sx(btn('soft') + 'cursor:pointer;font-family:inherit;')}>{r.code ? 'Replace' : 'Set'}</button>
            </form>
          ) : null}
          {err?.id === r.residentId && <ErrLine error={err.e} style="flex-basis:100%;margin-top:0;" />}
        </div>
      ))}
    </Block>
  );
}

// ── Package and Rx codes ──────────────────────────────────────────────────────

const EMPTY_PKG = { codeType: 'rx' as PackageCodeType, code: '', drug: '', strength: '', route: '', residentId: '' };

function Packages({ query, facility, canEdit, why }: { query: Q; facility: string | undefined; canEdit: boolean; why: string | null }) {
  const { client } = useSession();
  const list = useApi((c, s) => medPackages(c, { query, signal: s }), [facility]);
  const residents = useApi((c, s) => medWristbands(c, { query, signal: s }), [facility], canEdit);
  const [f, setF] = useState(EMPTY_PKG);
  const [err, setErr] = useState<ApiError | null>(null);
  const add = async () => {
    if (!canEdit) return;
    setErr(null);
    try {
      await medPackageAdd(client, {
        body: {
          code: f.code.trim(), codeType: f.codeType, drug: f.drug.trim(), strength: f.strength.trim(), route: f.route.trim(),
          ...(f.residentId ? { residentId: f.residentId } : {}),
        },
      });
      setF(EMPTY_PKG);
      list.reload();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const retire = async (p: MedPackage) => {
    if (!canEdit) return;
    setErr(null);
    try {
      await medPackageRetire(client, { params: { id: p.id } });
      list.reload();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  return (
    <Block title="Package and Rx codes" testId="med-pass-packages">
      <span style={sx(NOTE)}>
        What a package scans as: the Rx number on the pharmacy label, or the NDC on the package. These come from the pharmacy; typing one here is the fallback until the pharmacy feed is connected.
      </span>
      {!list.data && !list.error && <Skeleton rows={2} label="Loading the package codes" />}
      <ErrLine error={list.error} />
      {list.data?.packages.length === 0 && <span style={sx('font-size:16px;color:#54606C;')}>No package codes on file.</span>}
      {list.data?.packages.map((p) => (
        <div key={p.id} data-testid={`package-${p.id}`} style={sx(ROW)}>
          <span style={sx('flex:0 1 190px;min-width:0;')}>
            <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;font-variant-numeric:tabular-nums;')}>{p.code}</span>
            <span style={sx('display:block;font-size:16px;color:#54606C;')}>{CODE_TYPE[p.codeType ?? 'label']}</span>
          </span>
          <span style={sx('flex:1 1 220px;min-width:0;font-size:16px;color:#3C454F;')}>{`${p.drug} ${p.strength} · ${p.route}${p.resident ? ' · for ' + p.resident : ''}`}</span>
          <span style={sx(p.source === 'pharmacy' ? chip('#12503C', 'rgba(47,138,111,.16)') : chip('#54606C', 'rgba(34,39,46,.08)'))}>
            {p.source === 'pharmacy' ? 'FROM THE PHARMACY' : 'TYPED BY HAND'}
          </span>
          {canEdit && (
            <a href="#" aria-label={'Retire ' + p.code} onClick={(e) => { pre(e); void retire(p); }} style={sx(btn('ghost') + 'height:26px;padding:0 10px;font-size:16px;')}>
              Retire
            </a>
          )}
        </div>
      ))}
      {canEdit ? (
        <form onSubmit={(e) => { e.preventDefault(); void add(); }} data-testid="package-add" style={sx('display:flex;gap:8px;flex-wrap:wrap;align-items:center;padding-top:8px;border-top:1px solid rgba(34,39,46,.08);')}>
          <select aria-label="Kind of code" value={f.codeType} onChange={(e) => setF({ ...f, codeType: e.target.value as PackageCodeType })} style={sx(INPUT)}>
            {(Object.keys(CODE_TYPE) as PackageCodeType[]).map((k) => <option key={k} value={k}>{CODE_TYPE[k]}</option>)}
          </select>
          <input aria-label="Code" value={f.code} maxLength={120} placeholder={f.codeType === 'ndc' ? '0904-6712-61' : f.codeType === 'rx' ? 'Rx number' : 'What it scans as'} onChange={(e) => setF({ ...f, code: e.target.value })} style={sx(INPUT + 'flex:1 1 150px;')} />
          <input aria-label="Medicine" value={f.drug} maxLength={200} placeholder="Medicine" onChange={(e) => setF({ ...f, drug: e.target.value })} style={sx(INPUT + 'flex:1 1 150px;')} />
          <input aria-label="Strength" value={f.strength} maxLength={80} placeholder="Strength, e.g. 25 mg" onChange={(e) => setF({ ...f, strength: e.target.value })} style={sx(INPUT + 'flex:0 1 140px;')} />
          <input aria-label="Route" value={f.route} maxLength={60} placeholder="Route, e.g. by mouth" onChange={(e) => setF({ ...f, route: e.target.value })} style={sx(INPUT + 'flex:0 1 140px;')} />
          <select aria-label="Dispensed for" value={f.residentId} onChange={(e) => setF({ ...f, residentId: e.target.value })} style={sx(INPUT)}>
            <option value="">Any resident</option>
            {(residents.data?.residents ?? []).map((r) => <option key={r.residentId} value={r.residentId}>{r.name ?? 'Resident'}</option>)}
          </select>
          <button type="submit" style={sx(btn('soft') + 'cursor:pointer;font-family:inherit;')}>Add code</button>
        </form>
      ) : (
        why && <span style={sx('font-size:16px;color:#54606C;')}>{why}</span>
      )}
      <ErrLine error={err} />
    </Block>
  );
}

// ── Hold rules ────────────────────────────────────────────────────────────────

const EMPTY_RULE = { drug: '', vital: 'pulse' as HoldVital, comparator: '<' as HoldComparator, threshold: '' };

function HoldRules({ query, facility, canEdit, why, sample, onPolicy }: { query: Q; facility: string | undefined; canEdit: boolean; why: string | null; sample: boolean; onPolicy: () => void }) {
  const { client } = useSession();
  const list = useApi((c, s) => medHoldRules(c, { query, signal: s }), [facility, sample]);
  const [f, setF] = useState(EMPTY_RULE);
  const [err, setErr] = useState<ApiError | null>(null);
  const run = async (fn: () => Promise<unknown>, after: () => void) => {
    if (!canEdit) return;
    setErr(null);
    try {
      await fn();
      after();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const add = () => run(() => medHoldRuleAdd(client, { body: { drug: f.drug.trim(), vital: f.vital, comparator: f.comparator, threshold: Number(f.threshold) } }), () => { setF(EMPTY_RULE); list.reload(); });
  return (
    <Block title="Hold rules" testId="med-pass-hold-rules">
      <span style={sx(NOTE)}>A reading that holds a dose. The scan asks for the reading first, and a dose the rule holds is recorded as held.</span>
      {!list.data && !list.error && <Skeleton rows={2} label="Loading the hold rules" />}
      <ErrLine error={list.error} />
      {list.data?.rules.length === 0 && <span style={sx('font-size:16px;color:#54606C;')}>No hold rules in force.</span>}
      {list.data?.rules.map((r) => (
        <div key={r.id} data-testid={`hold-rule-${r.id}`} style={sx(ROW)}>
          {r.sample && <span style={sx(chip('#8A5A00', 'rgba(138,90,0,.12)'))}>SAMPLE</span>}
          <span style={sx('flex:1 1 300px;min-width:0;font-size:16px;color:#22272E;line-height:1.45;')}>{r.label}</span>
          {canEdit && !r.sample && (
            <a href="#" aria-label={'Retire rule: ' + r.label} onClick={(e) => { pre(e); void run(() => medHoldRuleRetire(client, { params: { id: r.id } }), list.reload); }} style={sx(btn('ghost') + 'height:26px;padding:0 10px;font-size:16px;')}>
              Retire
            </a>
          )}
        </div>
      ))}
      <div style={sx(ROW)}>
        <span style={sx('flex:1 1 300px;min-width:0;font-size:16px;color:#54606C;line-height:1.45;')}>
          The SAMPLE rule comes from the design, not from a prescriber. Turn it off once the building’s own rules are in.
        </span>
        <Switch on={sample} label={'SAMPLE rule ' + (sample ? 'on' : 'off')} disabled={!canEdit} title={why ?? undefined} onToggle={() => void run(() => medPassPolicySet(client, { body: { sampleHoldRules: !sample } }), onPolicy)} />
      </div>
      {canEdit && (
        <form onSubmit={(e) => { e.preventDefault(); void add(); }} data-testid="hold-rule-add" style={sx('display:flex;gap:8px;flex-wrap:wrap;align-items:center;padding-top:8px;border-top:1px solid rgba(34,39,46,.08);')}>
          <span style={sx('font-size:16px;color:#3C454F;')}>Hold</span>
          <input aria-label="Medicine to hold" value={f.drug} maxLength={120} placeholder="metoprolol" onChange={(e) => setF({ ...f, drug: e.target.value })} style={sx(INPUT + 'flex:1 1 140px;')} />
          <span style={sx('font-size:16px;color:#3C454F;')}>when the</span>
          <select aria-label="Reading" value={f.vital} onChange={(e) => setF({ ...f, vital: e.target.value as HoldVital })} style={sx(INPUT)}>
            {VITALS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <span style={sx('font-size:16px;color:#3C454F;')}>is</span>
          <select aria-label="Comparison" value={f.comparator} onChange={(e) => setF({ ...f, comparator: e.target.value as HoldComparator })} style={sx(INPUT)}>
            {COMPARATORS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input aria-label="Threshold" value={f.threshold} inputMode="decimal" placeholder="55" onChange={(e) => setF({ ...f, threshold: e.target.value })} style={sx(INPUT + 'flex:0 1 80px;')} />
          <button type="submit" style={sx(btn('soft') + 'cursor:pointer;font-family:inherit;')}>Add rule</button>
        </form>
      )}
      <ErrLine error={err} />
    </Block>
  );
}
